<script>
import HeaderContentLayout from '@/router/layouts/header-content-layout';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import Repositories from '@/repositories';
import draggable from 'vuedraggable';
const SingleConfigRepository = Repositories.get('singleconfigs');
const ShippingLocationsRepository = Repositories.get('shippinglocations');

export default {
  page: {
    title: 'Organization',
  },
  components: {
    Loading,
    HeaderContentLayout,
    draggable,
  },
  data() {
    return {
      isLoading: false,
      organizationConfig: null,
      logoFile: null,
      shippingLocations: [],
      removedShippingLocations: [],
      taxes: [],
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    async loadData() {
      try {
        this.isLoading = true;

        const [singleConfigResp, shippingLocationsResp] = await Promise.all([
          SingleConfigRepository.get('OrganizationConfig'),
          ShippingLocationsRepository.getAll(),
        ]);

        this.organizationConfig = { ...singleConfigResp.data };
        this.shippingLocations = shippingLocationsResp.data.data;

        if (this.organizationConfig.taxes != null) {
          this.taxes = this.organizationConfig.taxes;
        } else {
          this.taxes = [];
        }
      } catch {
        this.$toast.errorToast(this.$t('errors.errorLoading'));
      } finally {
        this.isLoading = false;
      }
    },
    async saveConfig() {
      try {
        this.isLoading = true;

        let payload = {
          ...this.organizationConfig,
          taxes: this.taxes,
        };

        let resp = await SingleConfigRepository.update(
          { jsonString: JSON.stringify(payload) },
          'OrganizationConfig'
        );

        for (let location of this.shippingLocations) {
          if (location.idPublic != null) {
            await ShippingLocationsRepository.update(
              location,
              location.idPublic
            );
          } else {
            let resp = await ShippingLocationsRepository.create(location);
            location.idPublic = resp.data.idPublic;
          }
        }

        for (let locationGuid of this.removedShippingLocations) {
          await ShippingLocationsRepository.delete(locationGuid);
        }

        this.organizationConfig = { ...resp.data };

        this.removedShippingLocations = [];

        this.$store.dispatch('cache/setOrganizationConfig', {
          ...this.organizationConfig,
        });

        this.$toast.successToast(this.$t('general.saved'));
      } catch {
        this.$toast.errorToast(this.$t('errors.errorLoading'));
      } finally {
        this.isLoading = false;
      }
    },
    handleImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.convertToBase64(file);
      }
    },
    addAddress() {
      this.shippingLocations = [
        ...this.shippingLocations,
        { name: '', address: '', phoneNumber: '', email: '' },
      ];
    },
    removeAddress(index) {
      if (this.shippingLocations[index].idPublic != null) {
        this.removedShippingLocations.push(
          this.shippingLocations[index].idPublic
        );
      }

      this.shippingLocations.splice(index, 1);
    },
    addTaxe() {
      this.taxes = [...this.taxes, { name: '', value: 0, isCompound: false }];
    },
    removeTax(index) {
      this.taxes.splice(index, 1);
    },
    convertToBase64(file) {
      this.isLoading = true;
      const reader = new FileReader();
      reader.onload = () => {
        this.organizationConfig.logoBase64 = reader.result;
        this.isLoading = false;
      };
      reader.readAsDataURL(file); // Convert the file to a data URL (Base64 string)
    },
  },
};
</script>

<template>
  <HeaderContentLayout :title="$t('organizationConfig.organizationConfig')">
    <template v-slot:header>
      <b-button type="button" variant="success" @click="saveConfig">{{
        $t('general.save')
      }}</b-button>
    </template>

    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>

    <div v-if="organizationConfig != null" style="width: 600px">
      <h4>{{ $t('general.general') }}</h4>

      <b-form-group
        id="input-group-name"
        :label="$t('general.name')"
        label-for="input-name"
      >
        <b-form-input
          id="input-name"
          v-model="organizationConfig.name"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        id="input-group-address"
        :label="$t('general.address')"
        label-for="input-address"
      >
        <b-form-textarea
          id="input-address"
          rows="6"
          v-model="organizationConfig.address"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group
        id="input-group-phone-number"
        :label="$t('general.phoneNumber')"
        label-for="input-phone-number"
      >
        <b-form-input
          id="input-phone-number"
          v-model="organizationConfig.phoneNumber"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        id="input-group-email"
        :label="$t('general.email')"
        label-for="input-email"
      >
        <b-form-input
          id="input-email"
          v-model="organizationConfig.email"
        ></b-form-input>
      </b-form-group>

      <b-form-group
        id="input-group-logo"
        :label="$t('general.logo')"
        label-for="input-logo"
      >
        <b-form-file
          id="input-logo"
          v-model="logoFile"
          accept="image/*"
          :placeholder="''"
          @change="handleImageChange"
          custom
        >
        </b-form-file>
      </b-form-group>

      <div
        v-if="
          organizationConfig.logoBase64 != null &&
            organizationConfig.logoBase64.length > 0
        "
        class="flex-box"
      >
        <img
          :src="organizationConfig.logoBase64"
          width="500px"
          height="280px"
        />

        <b-button
          @click="organizationConfig.logoBase64 = ''"
          style="width: 100px; margin-left: 10px"
        >
          {{ $t('general.clear') }}
        </b-button>
      </div>

      <h4 class="mt-4">
        {{ $t('general.money') }}
      </h4>

      <b-form-group
        id="input-group-currency"
        :label="$t('organizationConfig.currency')"
        label-for="input-currency"
      >
        <b-form-select
          id="input-currency"
          v-model="organizationConfig.currency"
        >
          <b-form-select-option value="USD">
            {{ $t('organizationConfig.dollar') }}
          </b-form-select-option>
          <b-form-select-option value="EUR">
            {{ $t('organizationConfig.euro') }}
          </b-form-select-option>
          <b-form-select-option value="GBP">
            {{ $t('organizationConfig.pound') }}
          </b-form-select-option>
          <b-form-select-option value="CHF">
            {{ $t('organizationConfig.franc') }}
          </b-form-select-option>
          <b-form-select-option value="MXN">
            {{ $t('organizationConfig.peso') }}
          </b-form-select-option>
        </b-form-select>
      </b-form-group>

      <b-form-group>
        <div class="add-group">
          <label>Taxes</label>
          <i class="fas fa-plus" style="color: #29b515" @click="addTaxe()"></i>
        </div>
        <draggable v-model="taxes">
          <div v-for="(tax, index) in taxes" :key="index" class="tax-group">
            <i class="fas fa-grip-vertical" style="color: #606060"></i>
            <b-form-input
              type="text"
              v-model="tax.name"
              :placeholder="$t('general.name')"
            />
            <b-input-group style="max-width: 120px">
              <b-form-input
                type="number"
                v-model="tax.value"
                step="0.001"
                min="0"
                max="100"
                placeholder="%"
              />
              <b-input-group-append>
                <b-input-group-text>%</b-input-group-text>
              </b-input-group-append>
            </b-input-group>
            <b-form-checkbox v-model="tax.isCompound" style="display: none">
              {{ $t('organizationConfig.isCompound') }}
            </b-form-checkbox>
            <i
              class="bx bxs-trash-alt"
              style="color: #f00"
              @click="removeTax(index)"
            ></i>
          </div>
        </draggable>
      </b-form-group>

      <h4 class="mt-4">
        {{ $t('general.delivery') }}
      </h4>

      <b-form-group>
        <div class="add-group">
          <label>
            {{ $t('general.shippingLocations') }}
          </label>
          <i
            class="fas fa-plus"
            style="color: #29b515"
            @click="addAddress()"
          ></i>
        </div>

        <div
          v-for="(location, index) in shippingLocations"
          :key="index"
          class="addresses-group"
        >
          <b-form-input
            type="text"
            v-model="location.name"
            style="margin-bottom: 10px"
            :placeholder="$t('general.name')"
          />

          <b-form-textarea
            rows="6"
            v-model="location.address"
            style="margin-bottom: 10px"
          ></b-form-textarea>

          <b-form-input
            type="text"
            v-model="location.phoneNumber"
            style="margin-bottom: 10px"
            :placeholder="$t('general.phoneNumber')"
          />

          <b-form-input
            type="text"
            v-model="location.email"
            style="margin-bottom: 10px"
            :placeholder="$t('general.email')"
          />

          <b-button
            block
            variant="danger"
            @click="removeAddress(index)"
            style="margin-bottom: 10px"
          >
            {{ $t('general.delete') }}
          </b-button>
        </div>
      </b-form-group>
    </div>
  </HeaderContentLayout>
</template>

<style scoped>
.add-group {
  display: flex;
  justify-content: space-between;
}

i {
  font-size: 1.4em;
  cursor: pointer;
}

.tax-group {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
}

.addresses-group {
  border: 1px solid rgb(250, 250, 250);
  box-shadow: 0 0 14px -5px rgba(0, 0, 0, 0.22);
  border-radius: 5px;
  margin-bottom: 10px;
  padding: 10px;
}
</style>
